import React, { useState, useEffect, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Combobox } from '@appkit4/react-components';
import { Notification, toaster } from '@appkit4/react-components/notification';
import { UserContext } from '@contexts/UserProvider';
import { isUserAdmin } from '@utils/users';
import { getAllUsers } from '@config/getUserApis';
import { addAdminRole } from '@config/getAdminApis';
import './AddAdminModal.scss';

interface User {
    label: string;
    value: string;
    email: string;
    fullName: string;
}

interface AddAdminModalProps {
    visible: boolean;
    onCancel: () => void;
    onAdminAdded: () => void;
    existingAdmins: string[];
}

const AddAdminModal: React.FC<AddAdminModalProps> = ({ visible, onCancel, onAdminAdded, existingAdmins }) => {
    const [selectedUser, setSelectedUser] = useState<string>('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [usersArr, setUsersArr] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { user, userEmail } = useContext(UserContext);

    useEffect(() => {
        if (visible) {
            setSelectedUser('');
            setShowSuccess(false);

            getAllUsers().then((users) => {
                const userList = users
                    .filter((u: any) => u.email !== userEmail && !existingAdmins.includes(u.email))
                    .map((u: any) => ({
                        label: u.fullName,
                        value: u.email,
                        email: u.email,
                        fullName: u.fullName,
                    }));

                setUsersArr(userList);
            });
        }
    }, [visible, userEmail, existingAdmins]);

    const handleSendInvite = async () => {
        if (!isUserAdmin(user)) {
            toaster.notify(
                <Notification status="error" title="Access Denied" message="Only administrators can add admins" />
            );
            return;
        }

        if (selectedUser) {
            try {
                setIsLoading(true);
                const addedUserEmail = selectedUser;
                const result = await addAdminRole(selectedUser);

                if (result.success) {
                    setShowSuccess(true);
                    setSelectedUser('');

                    const userList = usersArr.filter((u) => u.email !== addedUserEmail);
                    setUsersArr(userList);

                    setTimeout(() => {
                        onAdminAdded();
                    }, 5000);

                    setTimeout(() => {
                        setShowSuccess(false);
                    }, 5000);
                } else {
                    toaster.notify(<Notification status="error" title="Error" message="Failed to add admin" />);
                }
            } catch (error) {
                console.error('Error adding admin:', error);
                toaster.notify(<Notification status="error" title="Error" message="Failed to add admin" />);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleCancel = () => {
        setSelectedUser('');
        setShowSuccess(false);
        onCancel();
    };

    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    const handleComboboxSelect = (value: any) => {
        setSelectedUser(value);
        setShowSuccess(false);
    };

    const handleComboboxSearch = (value: string) => {
        console.log('Search:', value);
    };

    return (
        <Modal
            visible={visible}
            title="Add admin"
            ariaLabel="Add admin"
            onCancel={handleCancel}
            modalStyle={{ width: '500px' }}
            footerStyle={{ paddingTop: '8px', marginTop: '-8px', minHeight: '64px' }}
            footer={
                <>
                    <Button kind="secondary" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button kind="primary" onClick={handleSendInvite} disabled={!selectedUser || isLoading}>
                        {isLoading ? 'Sending...' : 'Send invite'}
                    </Button>
                </>
            }>
            <div className="add-admin-modal-content">
                {showSuccess && (
                    <div className="success-notification">
                        <div className="success-notification-content">
                            <span className="Appkit4-icon icon-checkmark-outline success-icon"></span>
                            <span className="success-text">Admin added</span>
                        </div>
                        <button className="close-button" onClick={handleCloseSuccess} aria-label="Close notification">
                            <span className="Appkit4-icon icon-close-outline"></span>
                        </button>
                    </div>
                )}
                <Combobox
                    data={usersArr}
                    value={selectedUser}
                    valueKey="email"
                    onSelect={handleComboboxSelect}
                    onSearch={handleComboboxSearch}
                    placeholder="Search for a user"
                    dropdownRenderMode="portal"
                />
            </div>
        </Modal>
    );
};

export default AddAdminModal;
